import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import NavBar from "@/components/NavBar";
import GamesList from "@/components/GamesList";
import { CreateGameForm } from "@/components";
import { BirdieGameGroup, Course } from "@/types";
import { getGames } from "@/integrations/supabase/game";
import { getUserCourses } from "@/integrations/supabase/profileCourseService";
import { dbCourseToCourse } from "@/integrations/supabase/courseService";
import { useAuth } from "@/context/AuthContext";
import { useHomeUser } from "@/hooks/useHomeUser";
import { toast } from "@/hooks/use-toast";

const Games = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedGame, setSelectedGame] = useState<BirdieGameGroup | null>(null);
  const [games, setGames] = useState<BirdieGameGroup[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const { user } = useAuth();
  const { currentUser } = useHomeUser();
  const navigate = useNavigate();
  const location = useLocation();
  
  const fetchGames = async () => {
    try {
      setIsLoading(true);
      const supabaseGames = await getGames();
      setGames(supabaseGames);
    } catch (error) {
      console.error("Error fetching games:", error);
      toast({
        title: "Error",
        description: "Failed to load games. Please try again.",
        variant: "destructive",
      });
      setGames([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, [location.key]);
  
  useEffect(() => {
    if (!user) return;
    const fetchCourses = async () => {
      try {
        setIsLoadingCourses(true);
        const coursesData = await getUserCourses(user.id);
        const formattedCourses = coursesData.map(course => dbCourseToCourse(course));
        setCourses(formattedCourses);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast({
          title: "Error",
          description: "Failed to load courses.",
          variant: "destructive",
        });
        setCourses([]);
      } finally {
        setIsLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [user]);
  
  useEffect(() => {
    const lastGameId = localStorage.getItem('lastOpenedGameId');
    if (lastGameId && window.location.pathname === "/") {
      const game = games.find(g => g.id === lastGameId);
      if (game) {
        handleSelectGame(game);
      }
    }
  }, [games]);
  
  const handleSelectGame = (game: BirdieGameGroup) => {
    setSelectedGame(game);
    localStorage.setItem('lastOpenedGameId', game.id);
    navigate(`/games/${game.id}`);
  };
  
  const handleCreateGame = () => {
    setShowCreateForm(true);
  };

  const handleGameCreated = async () => {
    const updatedGames = await getGames();
    setGames(updatedGames);
    setShowCreateForm(false);
    toast({
      title: "Success",
      description: "Game created successfully!",
    });
  };

  return (
    <div className="min-h-screen pb-16">
      <Header 
        title={showCreateForm ? "Create Game" : "Your Games"} 
        group={selectedGame || undefined}
      />
      
      {showCreateForm ? (
        <CreateGameForm
          courses={courses}
          onCreateGame={handleGameCreated}
          onCancel={() => setShowCreateForm(false)}
          isLoadingCourses={isLoadingCourses}
          defaultCourseId={currentUser?.homeCourseId || undefined}
        />
      ) : (
        <GamesList
          games={games}
          onSelectGame={handleSelectGame}
          onCreateGame={handleCreateGame}
          onRefresh={fetchGames}
          isLoading={isLoading}
        />
      )}
      
      <NavBar />
    </div>
  );
};

export default Games;
